import i18n from "i18next"; // ✅ Import i18n instance
import axiosInstance from "./axiosInstance";
import Swal from "sweetalert2";
import { login, logout, switchWorkspace, updateAccessToken, updateRefreshToken } from "../redux/slicers/authSlice";
import { persistor, store } from "../redux/store";
import { NavigateFunction } from 'react-router-dom';
import { clearStudentData, setSelectedStudentId, setStudents } from "../redux/slicers/studentSlice";
import { deleteTokenFCM } from "hooks/useFCM";


// Login Service - Stores Access Token in Redux and Refresh Token in Local Storage
export const loginService = async (email: string, password: string) => {
  try {
    const response = await axiosInstance.post("/auth/login", { email, password });

    if (response.status === 200) {
      const { user, accessToken, refreshToken, students, roles, isSuperAdmin, permissions, workSpaces } = response.data?.data;
      if (user.role === "guardian") {

        store.dispatch(setStudents(students));

        if (students.length > 0) {
          store.dispatch(setSelectedStudentId(students[0].id));
        }
      }
      // Dispatch login action to store user and tokens in Redux
      store.dispatch(login({ user, accessToken, refreshToken, roles, isSuperAdmin, permissions, workSpaces }));

      return { user, accessToken, refreshToken };
    }
  } catch (error: any) {
    throw error;
  }
};

// Refresh Access Token - Uses Refresh Token to Get a New Access Token
export const refreshAccessToken = async () => {
  try {
    // Get the current refresh token from Redux state
    const refreshToken = store.getState().authSlice.refreshToken;

    if (!refreshToken) {
      throw new Error("No refresh token found.");
    }

    // Call /auth/refresh with the refresh token in the request body
    const response = await axiosInstance.post("/auth/refresh", { refreshToken });

    if (response.status === 200) {
      const { newAccessToken, newRefreshToken } = response.data.data;

      // Update Redux with the new tokens
      store.dispatch(updateAccessToken(newAccessToken));
      store.dispatch(updateRefreshToken(newRefreshToken));

      return response.data.data;
    }
  } catch (error: any) {
    Swal.fire({
      icon: "error",
      title: i18n.t("session_expired"),
      text: i18n.t("please_login_again"),
    });
    store.dispatch(logout()); // Clear Redux state on error
    throw error;
  }
};

// Logout Service
export const logoutService = async (navigate: NavigateFunction) => {
  const accessToken = store.getState().authSlice.accessToken;
  if (accessToken) {
    deleteTokenFCM(accessToken).catch((err) => {
      console.error("Failed to delete FCM token", err);
      return;
    });
  }

  store.dispatch(logout()); // Clear authentication data
  store.dispatch(clearStudentData()) // Clear student Data
  await persistor.purge(); // From redux-persist

  navigate("/login"); // Redirect to login page
};

export const setPassword = async ({ token, password, type }: { token?: string; password: string, type?: string }) => {
  return await axiosInstance.post('/auth/set-password', { token, password, type });
};

export const validateToken = async (token?: string) => {
  const res = await axiosInstance.get(`/auth/validate-token`, {
    params: { token },
  });
  return res.data.data;
};

export const forgetPassword = async (email: string) => {
  const res = await axiosInstance.post(`/auth/forgot-password`, { email });
  return res.data;
};

export const resetPassword = async (token: string, password: string) => {
  const res = await axiosInstance.post(`/auth/reset-password`, { token, password });
  return res.data;
};

export const validateTokenResetPassword = async (token?: string) => {
  const res = await axiosInstance.get(`/auth/verify-reset-password-token?token=${token}`);
  return res.data;
};


export const switchWorkspaceService = async (instanceId: string) => {
  try {
    const response = await axiosInstance.post("/auth/switch-workspace", { instanceId });

    if (response.status === 200) {
      const { user, accessToken, refreshToken, students, roles, isSuperAdmin, permissions, workSpaces } = response.data?.data;
      store.dispatch(switchWorkspace({
        user,
        accessToken,
        refreshToken,
        roles,
        isSuperAdmin,
        permissions,
        workSpaces
      }));

      return { user, accessToken, refreshToken };
    }
  } catch (error: any) {
    throw error;
  }
};



